/**
 * 门店营收监控：按「一个门店一个 JSON」将浏览器中的数据持久化到 localStorage
 * Key: ms_store_revenue_store_${storeId}
 *
 * 数据结构（v1）：
 * - sources: 共享的源数据（多组件共用同一接口返回）
 * - components: 每个组件独立分支（自有数据源，单组件使用）
 *
 * 向后兼容：仍支持读取旧版扁平结构，写入时自动迁移到新结构
 *
 * 部署后查看 localStorage：Local Storage 按「当前页面的域名」隔离。
 * 在本地浏览器打开部署地址（如 https://xxx.com）时，需在 DevTools → Application → Local Storage
 * 中选中该域名（如 https://xxx.com），才能看到 ms_store_revenue_store_* 等 key；
 * 若选错域名（如 localhost 或 file://）则看不到部署站点的缓存。
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var PREFIX = 'ms_store_revenue_store_';
var STORAGE_VERSION = 1;
function storageKey(storeId) {
    return "".concat(PREFIX).concat(String(storeId));
}
/** 旧 slice 名 -> 新结构路径 */
var SLICE_TO_SOURCE = {
    storeDataFromCache: 'storeDataFromCache',
    operationAnalysis: 'operationAnalysis',
    businessReportDaily: 'businessReportDaily',
    futureRoomStatusList: 'futureRoomStatusList',
};
var SLICE_TO_COMPONENT = {
    channelPrice: 'channel-price',
    roomStatus: 'room-status',
    roomStatusRecently: 'room-status-recently',
    revpar: 'revpar',
};
function isNewStructure(raw) {
    if (raw == null || typeof raw !== 'object')
        return false;
    var o = raw;
    return o.version === STORAGE_VERSION && ('sources' in o || 'components' in o);
}
/** 从新结构生成兼容视图 */
function toView(data) {
    var _a, _b;
    var sources = (_a = data.sources) !== null && _a !== void 0 ? _a : {};
    var components = (_b = data.components) !== null && _b !== void 0 ? _b : {};
    return {
        channelPrice: components['channel-price'],
        roomStatus: components['room-status'],
        roomStatusRecently: components['room-status-recently'],
        revpar: components.revpar,
        storeDataFromCache: sources.storeDataFromCache,
        operationAnalysis: sources.operationAnalysis,
        businessReportDaily: sources.businessReportDaily,
        futureRoomStatusList: sources.futureRoomStatusList,
    };
}
/** 从旧版扁平结构迁移到新结构 */
function migrateFromLegacy(legacy) {
    var sources = {};
    var components = {};
    if (legacy.storeDataFromCache != null && typeof legacy.storeDataFromCache === 'object') {
        sources.storeDataFromCache = legacy.storeDataFromCache;
    }
    if (legacy.operationAnalysis != null && typeof legacy.operationAnalysis === 'object') {
        sources.operationAnalysis = legacy.operationAnalysis;
    }
    if (legacy.channelPrice != null && typeof legacy.channelPrice === 'object') {
        components['channel-price'] = legacy.channelPrice;
    }
    if (legacy.roomStatus != null && typeof legacy.roomStatus === 'object') {
        components['room-status'] = legacy.roomStatus;
    }
    if (legacy.roomStatusRecently != null && typeof legacy.roomStatusRecently === 'object') {
        components['room-status-recently'] = legacy.roomStatusRecently;
    }
    if (legacy.revpar != null && typeof legacy.revpar === 'object') {
        components.revpar = legacy.revpar;
    }
    if (legacy.futureRoomStatusList != null && typeof legacy.futureRoomStatusList === 'object') {
        sources.futureRoomStatusList = legacy.futureRoomStatusList;
    }
    return { version: STORAGE_VERSION, sources: sources, components: components };
}
/**
 * 读取某门店在 localStorage 中的完整 JSON
 * 返回兼容视图：channelPrice、roomStatus 等与旧版一致，支持新旧两种存储格式
 */
export function getStoreFromLocalStorage(storeId) {
    try {
        if (typeof localStorage === 'undefined')
            return null;
        var raw = localStorage.getItem(storageKey(storeId));
        if (raw == null)
            return null;
        var parsed = JSON.parse(raw);
        if (parsed == null || typeof parsed !== 'object')
            return null;
        var obj = parsed;
        if (isNewStructure(obj)) {
            return toView(obj);
        }
        return parsed;
    }
    catch (_a) {
        return null;
    }
}
/** store-data-from-cache 接口返回的 data 中，各字段与组件展示分支的对应关系 */
var STORE_DATA_SLICE_TO_COMPONENT = {
    orderTraffic: 'order-traffic',
    roomStatusRecently: 'room-status-recently',
    roomHealth: 'room-health',
    customerSource: 'customer-source',
    discountAnalysis: 'discount-analysis',
    channelAnalysis: 'channel-analysis',
    channelInfo: 'channel-info',
    channelInfoChart: 'channel-info-chart',
    occupancyAnalysis: 'occupancy-analysis',
    operationEfficiency: 'operation-efficiency',
    revPARChart: 'revpar-from-cache',
};
/**
 * 写入 store-data-from-cache：同时写入 sources（共享源数据）与 components 下各组件展示结构，便于核对完整性与正确性。
 */
export function setStoreDataFromCacheWithComponentSlices(storeId, data, fetchedAt) {
    try {
        if (typeof localStorage === 'undefined')
            return;
        var key = storageKey(storeId);
        var currentRaw = localStorage.getItem(key);
        var defaultCurrent = {
            version: STORAGE_VERSION,
            sources: {},
            components: {},
        };
        var current = defaultCurrent;
        if (currentRaw) {
            try {
                var parsed = JSON.parse(currentRaw);
                if (isNewStructure(parsed)) {
                    current = parsed;
                }
                else if (parsed != null && typeof parsed === 'object') {
                    current = migrateFromLegacy(parsed);
                }
            }
            catch (_a) {
                // 解析失败时使用默认
            }
        }
        var sources = __assign({}, current.sources);
        var components = __assign({}, current.components);
        sources.storeDataFromCache = { data: data, fetchedAt: fetchedAt };
        for (var _i = 0, _b = Object.entries(STORE_DATA_SLICE_TO_COMPONENT); _i < _b.length; _i++) {
            var _c = _b[_i], apiKey = _c[0], componentKey = _c[1];
            var slice = data[apiKey];
            if (slice !== undefined && slice !== null) {
                components[componentKey] = { data: slice, fetchedAt: fetchedAt };
            }
        }
        var next = {
            version: STORAGE_VERSION,
            sources: sources,
            components: components,
        };
        localStorage.setItem(key, JSON.stringify(next));
    }
    catch (_d) {
        // 忽略持久化失败
    }
}
/**
 * 写入某门店下的一块数据（合并到该门店的 JSON 中）
 * - storeDataFromCache / operationAnalysis -> sources（共享源数据）
 * - channelPrice / roomStatus / roomStatusRecently / revpar -> components（每组件独立分支）
 */
export function setStoreSliceInLocalStorage(storeId, slice, payload) {
    try {
        if (typeof localStorage === 'undefined')
            return;
        var key = storageKey(storeId);
        var currentRaw = localStorage.getItem(key);
        var defaultCurrent = {
            version: STORAGE_VERSION,
            sources: {},
            components: {},
        };
        var current = defaultCurrent;
        if (currentRaw) {
            try {
                var parsed = JSON.parse(currentRaw);
                if (isNewStructure(parsed)) {
                    current = parsed;
                }
                else if (parsed != null && typeof parsed === 'object') {
                    current = migrateFromLegacy(parsed);
                }
            }
            catch (_a) {
                // 解析失败时使用默认
            }
        }
        var sources = __assign({}, current.sources);
        var components = __assign({}, current.components);
        if (slice === 'operationAnalysis' && 'granularity' in payload) {
            var gran = payload.granularity;
            var nextOp = __assign({}, sources.operationAnalysis);
            nextOp[gran] = { data: payload.data, fetchedAt: payload.fetchedAt };
            sources.operationAnalysis = nextOp;
        }
        else if (SLICE_TO_SOURCE[slice]) {
            var k = SLICE_TO_SOURCE[slice];
            sources[k] = { data: payload.data, fetchedAt: payload.fetchedAt };
        }
        else if (SLICE_TO_COMPONENT[slice]) {
            var k = SLICE_TO_COMPONENT[slice];
            components[k] = { data: payload.data, fetchedAt: payload.fetchedAt };
        }
        var next = {
            version: STORAGE_VERSION,
            sources: sources,
            components: components,
        };
        localStorage.setItem(key, JSON.stringify(next));
    }
    catch (_b) {
        // 忽略持久化失败
    }
}
/**
 * store-data-from-cache 的有效期（5 分钟），用于判断是否可先用本地缓存再后台刷新
 */
export var STORE_DATA_FROM_CACHE_TTL_MS = 5 * 60 * 1000;
/**
 * 从 localStorage 读取 storeDataFromCache 切片，若存在且在 TTL 内则返回 { data, fetchedAt }
 * 用于「先展示缓存、再按需后台刷新」的加载策略
 */
export function getStoreDataFromCacheFromLocalStorage(storeId) {
    var view = getStoreFromLocalStorage(storeId);
    var slice = view === null || view === void 0 ? void 0 : view.storeDataFromCache;
    if (!(slice === null || slice === void 0 ? void 0 : slice.data) || typeof slice.fetchedAt !== 'number')
        return null;
    if (Date.now() - slice.fetchedAt > STORE_DATA_FROM_CACHE_TTL_MS)
        return null;
    if (typeof slice.data !== 'object' || slice.data === null)
        return null;
    return { data: slice.data, fetchedAt: slice.fetchedAt };
}
/**
 * 清除某门店在 localStorage 中的整条缓存（key: ms_store_revenue_store_${storeId}）。
 * 用于调试或「强制完全重新拉数」；刷新时仅清内存由 storeRevenueCacheClear 负责，不默认删 localStorage。
 */
export function clearStoreFromLocalStorage(storeId) {
    try {
        if (typeof localStorage === 'undefined')
            return;
        localStorage.removeItem(storageKey(storeId));
    }
    catch (_a) {
        // 忽略
    }
}
